/*
 * Copyright 2026 North Carolina State University
 */

import { isEmptyScheduleEntry } from '../../../utils/waterSchedule';

export const getNestedValue = (obj, path) => {
  if (!path) return undefined;
  return path.split('.').reduce((acc, part) => (acc == null ? acc : acc[part]), obj || {});
};

export const setNestedValue = (obj, path, value) => {
  if (!path) return obj;
  const parts = path.split('.');
  const next = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
  let cursor = next;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i];
    cursor[key] = (cursor[key] == null) ? {} : { ...cursor[key] };
    cursor = cursor[key];
  }
  cursor[parts[parts.length - 1]] = value;
  return next;
};

export const isFieldVisible = (field, formData) => {
  if (!field.showIf) return true;
  const v = getNestedValue(formData, field.showIf.field);
  switch (field.showIf.op) {
    case 'truthy': return Boolean(v);
    case 'falsy': return !v;
    case 'eq': return v === field.showIf.value;
    case 'neq': return v !== field.showIf.value;
    default: return true;
  }
};

export const buildInitialFormData = (schema, project = null, currentUser = null) => {
  const data = {};
  if (!schema?.sections) return data;
  for (const section of schema.sections) {
    for (const field of section.fields) {
      let value;
      if (field.type === 'repeater' || field.type === 'waterScheduleBuilder') {
        value = [];
      } else if (field.type === 'chamberAssignmentSelect') {
        value = null;
      } else if (field.type === 'datetimeAmPm') {
        value = field.defaultValue ?? { date: '', hour: '', minute: '', period: 'AM' };
      } else if (field.type === 'checkbox' || field.type === 'switch') {
        value = field.defaultValue ?? false;
      } else {
        value = field.defaultValue ?? '';
      }

      if (field.prefillFrom) {
        if (project && field.prefillFrom === 'project_title') {
          value = project.project_title || value;
        }
        if (project && field.prefillFrom === 'project_id') {
          value = project.project_id || value;
        }
        if (project && field.prefillFrom === 'pi_email') {
          const pi = project.users?.find(u => (typeof u.role === 'object' ? u.role?.role : u.role) === 'PI');
          value = pi?.email || pi?.user?.email || value;
        }
        if (project && field.prefillFrom === 'pi_name') {
          const pi = project.users?.find(u => (typeof u.role === 'object' ? u.role?.role : u.role) === 'PI');
          const u = pi?.user || pi || {};
          const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
          if (name) value = name;
        }
        if (currentUser && field.prefillFrom === 'current_user_name') {
          const name = `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim();
          if (name) value = name;
        }
        if (currentUser && field.prefillFrom === 'current_user_email') {
          if (currentUser.email) value = currentUser.email;
        }
      }
      Object.assign(data, setNestedValue(data, field.name, value));
    }
  }
  return data;
};

/**
 * Non-destructively fill empty readonly `prefillFrom` fields on an EXISTING
 * request's form_data (edit / admin_edit / view modes).
 *
 * Unlike create-time prefill, requestor fields are sourced from the request's
 * original submitter (not the current user, who may be an admin reviewing it).
 * Values the client actually entered are never overwritten.
 */
export const mergeReadonlyPrefill = (schema, formData, { project = null, request = null } = {}) => {
  if (!schema?.sections) return formData || {};
  let data = formData ? { ...formData } : {};

  const submitter = request?.submitted_by && typeof request.submitted_by === 'object'
    ? request.submitter || request.submitted_by
    : null;
  const submitterName = submitter
    ? `${submitter.first_name || ''} ${submitter.last_name || ''}`.trim()
    : '';

  const sourceFor = (prefillFrom) => {
    switch (prefillFrom) {
      case 'project_title':
        return request?.project_title || project?.project_title;
      case 'project_id':
        return (request?.project_id && typeof request.project_id === 'object'
          ? request.project_id.project_id
          : undefined) || project?.project_id;
      case 'pi_name':
        return request?.pi_name;
      case 'pi_email':
        return request?.pi_email;
      case 'current_user_name':
        return submitterName || undefined;
      case 'current_user_email':
        return submitter?.email;
      default:
        return undefined;
    }
  };

  for (const section of schema.sections) {
    for (const field of section.fields || []) {
      if (!field.readonly || !field.prefillFrom) continue;
      const existing = getNestedValue(data, field.name);
      if (existing !== undefined && existing !== null && existing !== '') continue;
      const value = sourceFor(field.prefillFrom);
      if (value !== undefined && value !== null && value !== '') {
        data = setNestedValue(data, field.name, value);
      }
    }
  }
  return data;
};

/**
 * Does the user count as having filled this in? Objects and arrays are walked,
 * so an object of empty strings reads as empty and so does a list of blank
 * schedule rows. MIRRORS the same helper in the backend's schema validator.
 */
const hasMeaningfulValue = (value) => {
  if (value === undefined || value === null || value === '' || value === false) return false;
  if (Array.isArray(value)) {
    return value.some(v => (isScheduleShaped(v) ? !isEmptyScheduleEntry(v) : hasMeaningfulValue(v)));
  }
  if (typeof value === 'object') return Object.values(value).some(hasMeaningfulValue);
  return true;
};

const isScheduleShaped = (v) =>
  v && typeof v === 'object' && !Array.isArray(v)
  && ('week1' in v || 'week2' in v || 'times_per_day' in v || 'water' in v);

export const validateAgainstSchema = (schema, formData) => {
  const errors = [];
  if (!schema?.sections) return errors;

  for (const section of schema.sections) {
    for (const field of section.fields) {
      if (!isFieldVisible(field, formData)) continue;

      if (field.type === 'repeater') {
        const arr = getNestedValue(formData, field.name);
        if (field.required && (!Array.isArray(arr) || arr.length < (field.minItems || 1))) {
          errors.push(`${field.label} requires at least ${field.minItems || 1} item(s)`);
        }
        continue;
      }

      if (field.type === 'chamberAssignmentSelect') {
        if (field.required) {
          const v = getNestedValue(formData, field.name);
          if (!v || !v.id) errors.push(`${field.label} is required`);
        }
        continue;
      }

      if (field.type === 'datetimeAmPm') {
        if (field.required) {
          const v = getNestedValue(formData, field.name);
          if (!v || !v.date || !v.hour || !v.minute || !v.period) {
            errors.push(`${field.label} is required (date, hour, minute, AM/PM)`);
          }
        }
        continue;
      }

      if (field.type === 'waterScheduleBuilder') {
        if (field.required) {
          const arr = getNestedValue(formData, field.name);
          if (!Array.isArray(arr) || !arr.some(e => !isEmptyScheduleEntry(e))) {
            errors.push(`${field.label} is required`);
          }
        }
        continue;
      }

      if (field.required) {
        const v = getNestedValue(formData, field.name);
        if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) {
          errors.push(`${field.label} is required`);
        }
      }
    }
  }

  // "At least one of these" — for a form where no single field can be
  // mandatory (a request may be about conditions OR watering) but an empty
  // submission is still meaningless. The server applies the same rule.
  if (schema.requireAnyOf?.fields?.length) {
    const anyFilled = schema.requireAnyOf.fields
      .some(name => hasMeaningfulValue(getNestedValue(formData, name)));
    if (!anyFilled) {
      errors.push(schema.requireAnyOf.message || 'Please fill in at least one of the requested changes.');
    }
  }

  return errors;
};
